import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Check, Cpu, Database, KeyRound, MonitorCog, ShieldCheck, Trash2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Chip } from "../../components/Chip";
import { Dialog } from "../../components/Dialog";
import { FormField } from "../../components/FormField";
import { Notice } from "../../components/Notice";
import { PageHeader } from "../../components/PageHeader";
import { Segmented } from "../../components/Segmented";
import { Switch } from "../../components/Switch";
import { decryptApiKey, encryptApiKey, verifyEncryptedApiKey } from "../../lib/ai/byok";
import { db, defaultSettings, requestPersistentStorage } from "../../lib/db";
import { useAppStore, type AppTheme } from "../../store/appStore";
import type { AppSettings } from "../../types";

type Panel = "security" | "provider" | "storage" | "appearance";
type AiDataAccessKey = keyof NonNullable<AppSettings["aiDataAccess"]>;

const panels: { id: Panel; label: string; icon: LucideIcon }[] = [
  { id: "security", label: "Security", icon: KeyRound },
  { id: "provider", label: "Provider", icon: Cpu },
  { id: "storage", label: "Storage", icon: Database },
  { id: "appearance", label: "Appearance", icon: MonitorCog },
];

const defaultAiDataAccess = {
  jobs: false,
  riskFlags: false,
  contact: false,
};

export function SettingsPage() {
  const settings = useLiveQuery(() => db.settings.get("local"));
  const theme = useAppStore((state) => state.theme);
  const setTheme = useAppStore((state) => state.setTheme);
  const [panel, setPanel] = useState<Panel>("security");
  const [apiKey, setApiKey] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const current = settings ?? defaultSettings();
  const aiDataAccess = current.aiDataAccess ?? defaultAiDataAccess;

  async function saveSettings(patch: Partial<AppSettings>) {
    await db.settings.put({ ...current, ...patch, updatedAt: new Date().toISOString() });
  }

  async function saveProvider(provider: AppSettings["aiProvider"]) {
    setError(null);
    if (provider !== "none" && !current.encryptedApiKey) {
      setError("Encrypt and verify a local API key before enabling a BYOK provider.");
      setPanel("security");
      return;
    }
    await saveSettings({ aiProvider: provider });
    setMessage(`Provider set to ${provider}.`);
  }

  async function saveEncryptedKey() {
    setError(null);
    setMessage(null);
    try {
      const encrypted = await encryptApiKey(apiKey, passphrase);
      const decrypted = await decryptApiKey(encrypted.cipherText, encrypted.iv, passphrase);
      if (decrypted !== apiKey) {
        throw new Error("Local encryption round trip failed.");
      }
      await saveSettings({
        encryptedApiKey: encrypted.cipherText,
        encryptedApiKeyIv: encrypted.iv,
        encryptedApiKeyKdfVersion: encrypted.kdfVersion,
        encryptedApiKeyCryptoVersion: encrypted.cryptoVersion,
        encryptedApiKeyIterations: encrypted.iterations,
        encryptedApiKeyUpdatedAt: new Date().toISOString(),
      });
      setApiKey("");
      setPassphrase("");
      setMessage("API key encrypted and verified locally. The passphrase was not stored.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Key save failed.");
    }
  }

  async function testStoredKey() {
    setError(null);
    setMessage(null);
    if (!current.encryptedApiKey || !current.encryptedApiKeyIv) {
      setError("No encrypted key is stored.");
      return;
    }
    const ok = await verifyEncryptedApiKey(
      current.encryptedApiKey,
      current.encryptedApiKeyIv,
      passphrase,
    );
    if (!ok) {
      setError("Stored key could not be decrypted with that passphrase.");
      return;
    }
    setMessage("Stored key decrypts successfully. No network request was made.");
    setPassphrase("");
  }

  async function removeKey() {
    await saveSettings({
      aiProvider: "none",
      encryptedApiKey: undefined,
      encryptedApiKeyIv: undefined,
      encryptedApiKeyKdfVersion: undefined,
      encryptedApiKeyCryptoVersion: undefined,
      encryptedApiKeyIterations: undefined,
      encryptedApiKeyUpdatedAt: undefined,
    });
    setConfirmRemove(false);
    setMessage("Encrypted BYOK key removed from local settings.");
  }

  async function persistStorage() {
    const persisted = await requestPersistentStorage();
    setMessage(
      persisted
        ? "Browser granted persistent storage for this origin."
        : "Persistent storage was not granted by this browser.",
    );
  }

  async function saveAiAccess(key: AiDataAccessKey, value: boolean) {
    await saveSettings({ aiDataAccess: { ...defaultAiDataAccess, ...aiDataAccess, [key]: value } });
  }

  return (
    <div className="space-y-7">
      <PageHeader
        action={
          <Chip
            icon={
              <KeyRound aria-hidden="true" className="h-4 w-4 text-accent-700" strokeWidth={1.5} />
            }
            size="md"
          >
            Key {current.encryptedApiKey ? "saved" : "none"} - Provider {current.aiProvider}
          </Chip>
        }
        eyebrow="Settings"
        title="Security and privacy settings"
      >
        AI features stay off until you add and encrypt your own key. No shared developer key is
        bundled in the app.
      </PageHeader>

      {message ? (
        <Notice title="Status" tone="success">
          {message}
        </Notice>
      ) : null}
      {error ? (
        <Notice title="Settings error" tone="danger">
          {error}
        </Notice>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[14rem_1fr] lg:items-start">
        <div
          className="card-hairline p-2 lg:sticky lg:top-24"
          aria-label="Settings panels"
          role="group"
        >
          <div className="grid gap-1 sm:hidden">
            <Segmented
              ariaLabel="Settings panels"
              options={panels.map((item) => ({
                value: item.id,
                label: item.label,
                icon: item.icon,
              }))}
              value={panel}
              onChange={setPanel}
            />
          </div>
          <div className="hidden gap-1 sm:grid">
            {panels.map((item) => (
              <Button
                key={item.id}
                aria-pressed={panel === item.id}
                className={`w-full justify-start ${
                  panel === item.id
                    ? "border-l-2 border-l-accent-700 bg-surface-muted/70 text-text"
                    : ""
                }`}
                iconLeft={<item.icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
                size="sm"
                variant="ghost"
                onClick={() => setPanel(item.id)}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="min-w-0">
          {panel === "security" ? (
            <div className="grid gap-5 lg:grid-cols-[1fr_24rem]">
              <Card className="grid gap-4" padding="lg" variant="surface">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised text-accent-700">
                    <KeyRound aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="page-eyebrow">Private key storage</p>
                    <h2 className="mt-2 text-xl font-semibold text-text">Encrypt BYOK API key</h2>
                    <p className="mt-2 text-sm leading-6 text-text-muted">
                      Your passphrase encrypts the key in this browser. The passphrase is never
                      stored.
                    </p>
                  </div>
                </div>
                <FormField
                  id="apiKey"
                  label="API key"
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.currentTarget.value)}
                />
                <FormField
                  id="passphrase"
                  label="Device master passphrase"
                  type="password"
                  autoComplete="current-password"
                  hint="Minimum 12 characters. Recovery is not possible if you lose it."
                  value={passphrase}
                  onChange={(event) => setPassphrase(event.currentTarget.value)}
                />
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => void saveEncryptedKey()}>
                    <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                    Encrypt locally
                  </Button>
                  <Button variant="secondary" onClick={() => void testStoredKey()}>
                    Test stored key
                  </Button>
                  <Button variant="danger" onClick={() => setConfirmRemove(true)}>
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                    Remove key
                  </Button>
                </div>
              </Card>

              <Card className="grid gap-4" padding="lg" variant="surface">
                <h2 className="text-xl font-semibold text-text">Key status</h2>
                {current.encryptedApiKey ? (
                  <dl className="grid gap-3 text-sm">
                    {[
                      ["State", "Encrypted key present"],
                      ["Version", current.encryptedApiKeyCryptoVersion ?? "legacy"],
                      ["Iterations", String(current.encryptedApiKeyIterations ?? "unknown")],
                      [
                        "Updated",
                        current.encryptedApiKeyUpdatedAt
                          ? new Date(current.encryptedApiKeyUpdatedAt).toLocaleString()
                          : "Unknown",
                      ],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="grid grid-cols-[7rem_1fr] gap-3 border-b border-border px-3 py-2 last:border-b-0"
                      >
                        <dt className="font-semibold text-text-subtle">{label}</dt>
                        <dd className="font-mono text-text">
                          {label === "State" ? <Chip tone="success">{value}</Chip> : value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <Notice title="No key stored" tone="warning">
                    AI provider activation is blocked until a key is encrypted locally.
                  </Notice>
                )}
              </Card>
            </div>
          ) : null}

          {panel === "provider" ? (
            <div className="grid gap-5 lg:grid-cols-2">
              <Card className="grid gap-4" padding="lg" variant="surface">
                <h2 className="text-xl font-semibold text-text">AI provider</h2>
                <p className="text-sm leading-6 text-text-muted">
                  Provider calls are the only approved external network boundary. Use none for
                  browser-only help.
                </p>
                <Segmented
                  ariaLabel="AI provider"
                  options={(["none", "openai", "anthropic"] as AppSettings["aiProvider"][]).map(
                    (provider) => ({
                      value: provider,
                      label: provider,
                      icon: current.aiProvider === provider ? Check : undefined,
                    }),
                  )}
                  value={current.aiProvider}
                  onChange={(provider) => void saveProvider(provider)}
                />
              </Card>

              <Card className="grid gap-4" padding="lg" variant="surface">
                <h2 className="text-xl font-semibold text-text">AI data access</h2>
                <p className="text-sm leading-6 text-text-muted">
                  These toggles do not send data by themselves. They control what a future
                  user-started AI action may include.
                </p>
                {(
                  [
                    ["jobs", "Job title, summary, location, budget, and timing"],
                    ["riskFlags", "Safety notes from the job"],
                    ["contact", "Contact fields and send notes"],
                  ] as [AiDataAccessKey, string][]
                ).map(([key, label]) => (
                  <Switch
                    key={key}
                    checked={Boolean(aiDataAccess[key as keyof typeof aiDataAccess])}
                    label={label}
                    onChange={(event) => void saveAiAccess(key, event.currentTarget.checked)}
                  />
                ))}
              </Card>
            </div>
          ) : null}

          {panel === "storage" ? (
            <Card className="grid gap-4" padding="lg" variant="surface">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised text-accent-700">
                  <Database aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <h2 className="text-xl font-semibold text-text">Storage posture</h2>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    Browser storage can be cleared by users, private mode, or device cleanup. Export
                    remains the backup path.
                  </p>
                </div>
              </div>
              <div>
                <Button variant="secondary" onClick={() => void persistStorage()}>
                  Request persistent browser storage
                </Button>
              </div>
            </Card>
          ) : null}

          {panel === "appearance" ? (
            <Card className="grid gap-4" padding="lg" variant="surface">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-raised text-accent-700">
                  <MonitorCog aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <h2 className="text-xl font-semibold text-text">Appearance</h2>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    Choose the visual mode for this browser. System follows the device preference.
                  </p>
                </div>
              </div>
              <Segmented<AppTheme>
                ariaLabel="Appearance"
                options={[
                  { value: "system", label: "System", icon: MonitorCog },
                  { value: "light", label: "Light" },
                  { value: "dark", label: "Dark" },
                ]}
                size="lg"
                value={theme}
                onChange={setTheme}
              />
            </Card>
          ) : null}
        </div>
      </div>

      <Dialog
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void removeKey()}>
              Remove encrypted key
            </Button>
          </>
        }
        description="This disables BYOK providers and removes the encrypted key material stored in IndexedDB."
        onOpenChange={setConfirmRemove}
        open={confirmRemove}
        tone="danger"
        title="Remove BYOK key"
      >
        <p className="text-sm leading-6 text-text-muted">
          This action does not contact an AI provider. It only changes local browser storage on this
          device.
        </p>
      </Dialog>
    </div>
  );
}
